// src/pages/creator-panel/LiveConversation/Tools/ToolLogsDrawer.js
import React, { useEffect, useState, useCallback } from 'react';
import axiosInstance from '../../../../utils/axiosInstance';

import {
    Drawer, Box, Typography, IconButton, CircularProgress, Paper, Chip,
    Accordion, AccordionSummary, AccordionDetails, Pagination, FormControlLabel, Switch,
} from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

const statusColor = (code) => {
    if (code == null) return 'default';
    if (code < 300) return 'success';
    if (code < 400) return 'warning';
    return 'error';
};

export default function ToolLogsDrawer({ open, onClose, tool, chatbotId }) {
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [errorsOnly, setErrorsOnly] = useState(false);
    const [loading, setLoading] = useState(false);

    const fetchLogs = useCallback(async () => {
        if (!open || !chatbotId || !tool?.id) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(
                `/api/chatbots/${chatbotId}/live-conversation/tools/logs`,
                { params: { tool_id: tool.id, page, limit: 20, errors_only: errorsOnly ? '1' : '' } }
            );
            setLogs(res.data?.data || []);
            setTotal(res.data?.total || 0);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [open, chatbotId, tool?.id, page, errorsOnly]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);
    useEffect(() => { if (open) setPage(1); }, [open, tool?.id]);

    const totalPages = Math.max(1, Math.ceil(total / 20));

    return (
        <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 560 } } }}>
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h6">Tool Call Logs</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {tool?.display_name || tool?.name}
                    </Typography>
                </Box>
                <FormControlLabel
                    control={<Switch size="small" checked={errorsOnly} onChange={(e) => { setErrorsOnly(e.target.checked); setPage(1); }} />}
                    label="Errors only" />
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </Box>

            <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={24} /></Box>
                ) : logs.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                        No call logs yet.
                    </Typography>
                ) : (
                    logs.map((log) => (
                        <Accordion key={log.id} disableGutters sx={{ mb: 1 }}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                    <Chip size="small" color={log.success ? 'success' : 'error'}
                                        label={log.success ? 'OK' : 'FAIL'} />
                                    {log.response_status != null && (
                                        <Chip size="small" color={statusColor(log.response_status)}
                                            label={`HTTP ${log.response_status}`} />
                                    )}
                                    {log.latency_ms != null && (
                                        <Chip size="small" variant="outlined" label={`${log.latency_ms}ms`} />
                                    )}
                                    {log.channel && <Chip size="small" variant="outlined" label={log.channel} />}
                                    <Box sx={{ flex: 1 }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {new Date(log.created_at).toLocaleString()}
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                {log.session_code && (
                                    <Typography variant="caption" sx={{ display: 'block', mb: 1 }}>
                                        Session: <code>{log.session_code}</code>
                                    </Typography>
                                )}
                                {log.request_url && (
                                    <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
                                        <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                            {log.request_url}
                                        </Typography>
                                    </Paper>
                                )}
                                <Typography variant="subtitle2">Parameters</Typography>
                                <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'grey.50' }}>
                                    <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(log.parameters_used || {}, null, 2)}
                                    </pre>
                                </Paper>
                                {log.error_message && (
                                    <>
                                        <Typography variant="subtitle2" color="error">Error</Typography>
                                        <Paper variant="outlined" sx={{ p: 1, mb: 1, bgcolor: 'error.light', color: 'error.contrastText' }}>
                                            <Typography variant="body2">{log.error_message}</Typography>
                                        </Paper>
                                    </>
                                )}
                                {log.response_body_snippet && (
                                    <>
                                        <Typography variant="subtitle2">Response</Typography>
                                        <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.50' }}>
                                            <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                                {log.response_body_snippet}
                                            </pre>
                                        </Paper>
                                    </>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))
                )}
            </Box>

            {total > 20 && (
                <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'center' }}>
                    <Pagination size="small" count={totalPages} page={page} onChange={(_, p) => setPage(p)} />
                </Box>
            )}
        </Drawer>
    );
}
